// src/pages/markets/board/board/ProfileDialogs.tsx
import { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    TextField,
} from '@mui/material';
import type { BoardProfile } from '../state/profileTypes';
import { KEEP_DIALOG_SIZE } from '../../../../root/touchTheme';

export function DeleteProfileDialog({
    profile,
    onCancel,
    onConfirm,
}: {
    profile: BoardProfile | null;
    onCancel: () => void;
    onConfirm: () => void;
}) {
    if (!profile) return null;
    return (
        <Dialog
            open
            onClose={onCancel}
            maxWidth="xs"
            fullWidth
            className={KEEP_DIALOG_SIZE}
        >
            <DialogTitle sx={{ fontSize: '15px' }}>Delete “{profile.name}”?</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ fontSize: '13px' }}>
                    The profile and its saved filters are removed. Your board keeps the settings that
                    are on it right now.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                <Button color="error" variant="contained" onClick={onConfirm}>
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export function PromoteProfileDialog({
    profile,
    shared,
    onCancel,
    onConfirm,
}: {
    profile: BoardProfile | null;
    /** Existing shared profiles — a name collision replaces one of these. */
    shared: BoardProfile[];
    onCancel: () => void;
    onConfirm: (name: string, description: string) => void;
}) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');

    useEffect(() => {
        setName(profile?.name ?? '');
        setDescription('');
    }, [profile]);

    if (!profile) return null;

    // Promoting onto an existing shared name replaces that profile rather than
    // creating a duplicate, so the dialog has to say whose settings it overwrites.
    const clash = shared.find(
        p => p.name.trim().toLowerCase() === name.trim().toLowerCase(),
    );

    return (
        <Dialog
            open
            onClose={onCancel}
            maxWidth="xs"
            fullWidth
            className={KEEP_DIALOG_SIZE}
        >
            <DialogTitle sx={{ fontSize: '15px' }}>
                {clash ? 'Replace a shared profile' : 'Promote to a shared profile'}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Name"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        size="small"
                        fullWidth
                    />
                    <TextField
                        label="Description"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        helperText="Shown as a tooltip to everyone who can see the board."
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                    />
                    {clash && (
                        <Alert severity="warning" sx={{ fontSize: '12px' }}>
                            A shared profile called “{clash.name}” already exists. Confirming
                            replaces its settings for everyone who uses it.
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                <Button
                    variant="contained"
                    color={clash ? 'warning' : 'primary'}
                    disabled={!name.trim() || !description.trim()}
                    onClick={() => onConfirm(name.trim(), description.trim())}
                >
                    {clash ? 'Replace' : 'Promote'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
